import 'assets/style/style.css';

import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { TabContent, TabPane } from 'reactstrap';
import {
    FaServer,
    FaGlobe,
    FaMobileAlt,
    FaFolderOpen,
    FaDatabase,
    FaExchangeAlt,
    FaHistory,
    FaEraser,
    FaRocket,
    FaNetworkWired,
    FaTruck,
    FaTachometerAlt,
    FaBook,
    FaTable,
    FaChartBar,
    FaTasks,
    FaClock,
    FaBars,
    FaHome,
    FaChevronLeft,
    FaChevronDown,
    FaChevronUp,
    FaStar,
    FaRegStar,
    FaRocketchat,
    FaChevronRight,
    FaCalendarAlt
} from 'react-icons/fa';
import { MdRocketLaunch } from 'react-icons/md';
import { TbFileTypeSql, TbApi } from 'react-icons/tb';
import { IoMdRocket } from 'react-icons/io';

// import AdminDashboardServer from 'components/AdminDashboardServer';
import AdminDashboardTableComponent from './components/AdminDashboardTableComponent';
import DBWorker from 'components/DBWorker';
import DBComparison from 'components/DBComparison/DBComparison';
import UpdateLog from 'components/UpdateLog/UpdateLog';

import AdminDashboardViewModel from './AdminDashboard.viewmodel';
import ClearTransaction from '../ClearTransaction/ClearTransaction.view';
import Migration from '../Migration/Migration.view';
import TransportRequestUATView from '../TransportRequestUpdate/TransportRequestUAT.view';
import MonitorPerformance from '../MonitorPerformance/MonitorPerformance.view';
import F1ApiLogView from '../F1ApiLog/F1ApiLog.view';
import { serverMonitorColumns } from './data/serverMonitorColumns';
import WebMonitorDashboard from './components/WebMonitorDashboard';
import FAPPMonitorDashboard from './components/FAPPMonitorDashboard';
import FTPMonitorDashboard from './components/FTPMonitorDashboard';
import PortConfigView from '../PortConfig/PortConfig.view';
import TransportRequestPRD from '../TransportRequestPRD/TransportRequestPRD.view';
import MonitorSizeTableView from '../MonitorSizeTableDatabase/MonitorSizeTableDatabase.view';
import MigrationPageView from '../MigrationPage/MigrationPage.view';
import DMSDashboard from '../DMSDashboard/DMSDashboard';
import NewMigration from '../NewMigration/NewMigration.view';
import WorkflowDashboard from '../WorkflowDashboard/WorkflowDashboard';
import WorkflowWaitingTimeView from '../WorkflowWaitingTime/WorkflowWaitingTime.view';
import F1ApiPortalView from '../F1ApiPortal/F1ApiPortal.view';

const MENU_ITEMS = [
    { id: '1', title: 'Server', icon: FaServer },
    { id: '13', title: 'Web', icon: FaGlobe },
    { id: '14', title: 'FAPP', icon: FaMobileAlt },
    { id: '15', title: 'sFTP', icon: FaFolderOpen },
    { id: '2', title: 'DB Worker', icon: FaDatabase, superAdminOnly: true },
    { id: '5', title: 'DB Comparison', icon: FaExchangeAlt, superAdminOnly: true },
    { id: '6', title: 'UpDate Log', icon: FaHistory, superAdminOnly: true },
    { id: '7', title: 'Clear Transaction', icon: FaEraser, superAdminOnly: true },
    { id: '18', title: 'Migration Script', icon: TbFileTypeSql },
    { id: '8', title: 'Migration', icon: IoMdRocket, superAdminOnly: true },
    { id: '21', title: 'New Migration', icon: FaRocketchat },
    { id: '16', title: 'Config Port', icon: FaNetworkWired, superAdminOnly: true },
    { id: '9', title: 'TransportRequest UAT', icon: FaTruck },
    { id: '10', title: 'TransportRequest PRD', icon: MdRocketLaunch },
    { id: '11', title: 'MonitorPerformance', icon: FaTachometerAlt, superAdminOnly: true },
    { id: '23', title: 'F1 Api Portal', icon: TbApi },
    { id: '12', title: 'F1 Api Log', icon: FaBook, superAdminOnly: true },
    { id: '17', title: 'Monitor Size Table', icon: FaTable, superAdminOnly: true },
    { id: '19', title: 'DMS Dashboard', icon: FaChartBar },
    { id: '20', title: 'Workflow Dashboard', icon: FaTasks },
    { id: '22', title: 'Workflow Waiting Time', icon: FaClock },
    { id: '24', title: 'Team Calendar', icon: FaCalendarAlt },
    { id: '25', title: 'Migration Runbook', icon: FaTasks }
];

const AdminDashboard = () => {
    const {
        activeTab,
        onSelectTab,
        handleClick,
        monitorServers,
        onchangeSwitch,
        autoRefresh,
        toggleRefresh,
        authenticationUser,
        favorites,
        toggleFavorite
    } = AdminDashboardViewModel();

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isAllMenuExpanded, setIsAllMenuExpanded] = useState(false);
    const [isFavoritesExpanded, setIsFavoritesExpanded] = useState(true);

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    const handleMenuClick = (id: string) => {
        onSelectTab(id);
        setIsSidebarOpen(false);
    };

    const visibleMenus = MENU_ITEMS.filter(
        item => !item.superAdminOnly || authenticationUser?.type === 'super_admin'
    );

    return (
        <div className="relative m-0 flex h-full w-full overflow-hidden border-t border-gray-200 bg-gray-50 p-0">
            {/* Overlay for Drawer */}
            {isSidebarOpen && (
                <div
                    className="absolute inset-0 z-20 bg-gray-900 bg-opacity-20 transition-opacity"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Hover Trigger Area */}
            {!isSidebarOpen && (
                <div
                    className="group absolute bottom-0 left-0 top-0 z-10 flex w-6 cursor-pointer items-center bg-transparent"
                    onClick={() => setIsSidebarOpen(true)}
                    title="Open Menu"
                >
                    <div className="-ml-0.5 flex h-24 w-1.5 items-center justify-center rounded-r-md bg-gray-300 opacity-60 shadow-sm transition-all duration-300 group-hover:ml-0 group-hover:w-5 group-hover:bg-blue-500 group-hover:opacity-100">
                        <FaChevronRight
                            size={12}
                            className="text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                        />
                    </div>
                </div>
            )}

            {/* Sidebar (Drawer) */}
            <div
                className={`absolute bottom-0 left-0 top-0 z-30 flex w-64 flex-col border-r border-gray-200 bg-white shadow-xl transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
                <div className="flex items-center justify-between border-b border-gray-200 p-4">
                    <span className="overflow-hidden whitespace-nowrap font-bold text-gray-700">
                        Menu
                    </span>
                    <button
                        onClick={toggleSidebar}
                        className="ml-auto rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100"
                    >
                        <FaChevronLeft size={14} />
                    </button>
                </div>
                <div className="custom-scrollbar flex-1 overflow-y-auto py-2">
                    <div
                        className={`flex cursor-pointer items-center px-4 py-3 transition-colors ${activeTab === '0' ? 'border-r-4 border-blue-600 bg-blue-50 text-blue-600' : 'border-r-4 border-transparent text-gray-600 hover:bg-gray-100'}`}
                        onClick={() => handleMenuClick('0')}
                        title="Home"
                    >
                        <FaHome
                            size={18}
                            className="min-w-[1.125rem]"
                        />
                        <span className="ml-3 truncate text-sm font-medium">Home</span>
                    </div>

                    {/* Favorites Section */}
                    {favorites.length > 0 && (
                        <div className="mt-2 pb-1">
                            <div
                                className="flex cursor-pointer items-center justify-between px-4 py-2 text-gray-500 transition-colors hover:bg-gray-50"
                                onClick={() => setIsFavoritesExpanded(!isFavoritesExpanded)}
                                title="Favorites"
                            >
                                <span className="text-xs font-semibold uppercase tracking-wider">
                                    Favorites
                                </span>
                                {isFavoritesExpanded ? (
                                    <FaChevronUp size={12} />
                                ) : (
                                    <FaChevronDown size={12} />
                                )}
                            </div>

                            {isFavoritesExpanded &&
                                visibleMenus
                                    .filter(m => favorites.includes(m.id))
                                    .map(menu => {
                                        const Icon = menu.icon;
                                        const isActive = activeTab === menu.id;
                                        return (
                                            <div
                                                key={`fav-${menu.id}`}
                                                className={`flex cursor-pointer items-center px-4 py-3 transition-colors ${isActive ? 'border-r-4 border-blue-600 bg-blue-50 text-blue-600' : 'border-r-4 border-transparent text-gray-600 hover:bg-gray-100'}`}
                                                onClick={() => handleMenuClick(menu.id)}
                                                title={menu.title}
                                            >
                                                <Icon
                                                    size={18}
                                                    className="min-w-[1.125rem]"
                                                />
                                                <span className="ml-3 truncate text-sm font-medium">
                                                    {menu.title}
                                                </span>
                                            </div>
                                        );
                                    })}
                        </div>
                    )}

                    {/* All Menus Section */}
                    <div className="mt-2 pb-1">
                        <div
                            className="flex cursor-pointer items-center justify-between px-4 py-2 text-gray-500 transition-colors hover:bg-gray-50"
                            onClick={() => setIsAllMenuExpanded(!isAllMenuExpanded)}
                            title="All Menus"
                        >
                            <span className="text-xs font-semibold uppercase tracking-wider">
                                All Menus
                            </span>
                            {isAllMenuExpanded ? (
                                <FaChevronUp size={12} />
                            ) : (
                                <FaChevronDown size={12} />
                            )}
                        </div>

                        {isAllMenuExpanded &&
                            visibleMenus.map(menu => {
                                const Icon = menu.icon;
                                const isActive = activeTab === menu.id;
                                return (
                                    <div
                                        key={menu.id}
                                        className={`flex cursor-pointer items-center px-4 py-3 transition-colors ${isActive ? 'border-r-4 border-blue-600 bg-blue-50 text-blue-600' : 'border-r-4 border-transparent text-gray-600 hover:bg-gray-100'}`}
                                        onClick={() => handleMenuClick(menu.id)}
                                        title={menu.title}
                                    >
                                        <Icon
                                            size={18}
                                            className="min-w-[1.125rem]"
                                        />
                                        <span className="ml-3 truncate text-sm font-medium">
                                            {menu.title}
                                        </span>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-1 flex-col overflow-hidden bg-gray-50 bg-opacity-50">
                <TabContent
                    activeTab={activeTab === '0' ? '0' : 'content'}
                    className="custom-scrollbar flex flex-1 flex-col overflow-y-auto p-0 lg:p-6"
                >
                    {/* Home - Cards View */}
                    <TabPane
                        tabId="0"
                        className={`${activeTab === '0' ? 'flex-1' : ''} p-3`}
                    >
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-800">Admin Dashboard</h2>
                            <p className="mt-1 text-gray-500">
                                Select a module to view or configure
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 pb-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-6 xl:grid-cols-5">
                            {visibleMenus.map(menu => {
                                const Icon = menu.icon;
                                return (
                                    <div
                                        key={menu.id}
                                        className="group relative flex transform cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm transition-shadow duration-200 hover:-translate-y-1 hover:shadow-md"
                                        onClick={() => onSelectTab(menu.id)}
                                    >
                                        <div
                                            className="absolute right-4 top-4 text-gray-300 transition-colors hover:text-yellow-400"
                                            onClick={e => toggleFavorite(menu.id, e)}
                                            title={
                                                favorites.includes(menu.id)
                                                    ? 'Remove from Favorites'
                                                    : 'Add to Favorites'
                                            }
                                        >
                                            {favorites.includes(menu.id) ? (
                                                <FaStar
                                                    size={20}
                                                    className="text-yellow-400"
                                                />
                                            ) : (
                                                <FaRegStar size={20} />
                                            )}
                                        </div>
                                        <div className="mb-4 rounded-full bg-blue-50 p-4 text-blue-500 transition-colors duration-200 group-hover:bg-blue-500 group-hover:text-white">
                                            <Icon size={28} />
                                        </div>
                                        <h3 className="font-semibold text-gray-700 transition-colors group-hover:text-blue-600">
                                            {menu.title}
                                        </h3>
                                    </div>
                                );
                            })}
                        </div>
                    </TabPane>

                    {/* Content View */}
                    <TabPane
                        tabId="content"
                        className={
                            activeTab !== '0'
                                ? 'flex min-h-full flex-1 flex-col border border-gray-200 bg-white p-4 shadow-sm'
                                : ''
                        }
                    >
                        {activeTab === '1' && (
                            <AdminDashboardTableComponent
                                columns={serverMonitorColumns}
                                data={monitorServers.ResponseAdminServer}
                                onCheckOnlineStatus={record => record.status === 'true'}
                                handleClickEvent={handleClick}
                                onToggleSwitch={onchangeSwitch}
                                isAutoRefresh={autoRefresh}
                                toggleRefresh={toggleRefresh}
                            />
                        )}
                        {activeTab === '13' && <WebMonitorDashboard />}
                        {activeTab === '14' && <FAPPMonitorDashboard />}
                        {activeTab === '15' && <FTPMonitorDashboard />}
                        {activeTab === '2' && <DBWorker />}
                        {activeTab === '5' && <DBComparison />}
                        {activeTab === '6' && <UpdateLog />}
                        {activeTab === '7' && <ClearTransaction />}
                        {activeTab === '8' && <Migration />}
                        {activeTab === '9' && <TransportRequestUATView />}
                        {activeTab === '10' && <TransportRequestPRD />}
                        {activeTab === '11' && <MonitorPerformance />}
                        {activeTab === '12' && <F1ApiLogView />}
                        {activeTab === '16' && <PortConfigView />}
                        {activeTab === '17' && <MonitorSizeTableView />}
                        {activeTab === '18' && <MigrationPageView />}
                        {activeTab === '19' && <DMSDashboard />}
                        {activeTab === '20' && <WorkflowDashboard />}
                        {activeTab === '21' && <NewMigration />}
                        {activeTab === '22' && <WorkflowWaitingTimeView />}
                        {activeTab === '23' && <F1ApiPortalView />}
                    </TabPane>
                </TabContent>
            </div>
        </div>
    );
};

export default observer(AdminDashboard);
